import { useCommonServiceCommonLocationsList } from '@insurance/api/queries'
import { useValidationsField } from '@insurance/hooks'
import { normalize } from '@shared/utils'
import { Col, Form, Input, Row, Select } from 'antd'
import { useTranslation } from 'react-i18next'

import { usePropertyInsuranceValues } from './PropertyInsuranceContext'
import { formProps } from './utils'

type Props = {
  fieldKey: number
}

export const ItemLocationFields = ({ fieldKey }: Props) => {
  const { t } = useTranslation()
  const { required } = useValidationsField()
  const formInstance = Form.useFormInstance()
  const { itemsKey } = formProps(t)
  const { isEditable } = usePropertyInsuranceValues()

  const country = Form.useWatch([itemsKey.name, fieldKey, 'country'])
  const district = Form.useWatch([itemsKey.name, fieldKey, 'district'])

  const { data: districts } = useCommonServiceCommonLocationsList({
    country: country || 'MD'
  })

  const districtsFound = districts?.filter(({ name }) =>
    normalize(name)?.includes(normalize(district))
  )

  const districtsOptions = districts?.map(({ name }) => ({ value: name }))

  const { data: locations } = useCommonServiceCommonLocationsList(
    {
      country: country || 'MD',
      district: districtsFound?.[0]?.id
    },
    undefined,
    { enabled: districtsFound?.length === 1 }
  )

  const locationsOptions = locations?.map(({ name }) => ({ value: name }))

  return (
    <Row gutter={[12, 12]}>
      <Col xs={24} lg={12} xl={8}>
        <Form.Item
          name={[fieldKey, 'district']}
          label={t('district')}
          rules={[required]}
        >
          <Select
            disabled={isEditable}
            placeholder={t('district')}
            showSearch
            filterOption={(input, option) =>
              normalize(option?.value)?.includes(normalize(input))
            }
            options={districtsOptions}
            onSelect={() =>
              formInstance.resetFields([[itemsKey.name, fieldKey, 'location']])
            }
          />
        </Form.Item>
      </Col>

      <Col xs={24} lg={12} xl={8}>
        <Form.Item
          name={[fieldKey, 'location']}
          label={t('location')}
          rules={[required]}
        >
          <Select
            placeholder={t('location')}
            showSearch
            filterOption={(input, option) =>
              normalize(option?.value)?.includes(normalize(input))
            }
            options={locationsOptions}
            disabled={isEditable}
          />
        </Form.Item>
      </Col>

      <Col xs={24} lg={12} xl={8}>
        <Form.Item name={[fieldKey, 'street']} label={t('street')}>
          <Input placeholder={t('street')} disabled={isEditable} />
        </Form.Item>
      </Col>

      <Col xs={8}>
        <Form.Item name={[fieldKey, 'number']} label={t('number')}>
          <Input placeholder={t('number')} disabled={isEditable} />
        </Form.Item>
      </Col>

      <Col xs={8}>
        <Form.Item name={[fieldKey, 'block']} label={t('block')}>
          <Input placeholder={t('block')} disabled={isEditable} />
        </Form.Item>
      </Col>

      <Col xs={8}>
        <Form.Item name={[fieldKey, 'apartment']} label={t('apartment')}>
          <Input placeholder={t('apartment')} disabled={isEditable} />
        </Form.Item>
      </Col>
    </Row>
  )
}
